/**
 * DateRangePicker Component
 *
 * Full-featured date range selector with:
 *  - Quick presets: Last 7d, 30d, 90d, 6 months, 12 months, All Time
 *  - Visual calendar grid for picking start/end dates
 *  - Month picker mode for selecting an entire month
 *  - Compare to previous period toggle
 *  - Clean dark theme, no gradients/glows
 *
 * Self-contained -- no external date picker libraries.
 * Integrates with Zustand store for global date range state.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import useMonarchStore from '@/stores/monarchStore';
import type { DateRangePreset } from '@/types/dashboard';

/* ============================================================================
   Constants
   ============================================================================ */

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/* ============================================================================
   Date Utilities
   ============================================================================ */

function ensureDate(value: Date | string | number): Date {
  if (value instanceof Date) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? new Date() : d;
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBetween(date: Date, start: Date, end: Date): boolean {
  const d = startOfDay(date).getTime();
  const s = startOfDay(start).getTime();
  const e = startOfDay(end).getTime();
  return d >= s && d <= e;
}

function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

function isFuture(date: Date): boolean {
  return startOfDay(date).getTime() > startOfDay(new Date()).getTime();
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function diffInHours(start: Date, end: Date): number {
  return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
}

function formatDisplayDate(date: Date | string | number): string {
  return ensureDate(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function subDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  return d;
}

function subMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
}

/* ============================================================================
   Preset Definitions
   ============================================================================ */

interface PresetOption {
  label: string;
  value: DateRangePreset;
  getRange: () => { start: Date; end: Date };
}

const QUICK_PRESETS: PresetOption[] = [
  {
    label: 'Last 7 days',
    value: '7d',
    getRange: () => ({
      start: startOfDay(subDays(new Date(), 6)),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: 'Last 30 days',
    value: '30d',
    getRange: () => ({
      start: startOfDay(subDays(new Date(), 29)),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: 'Last 90 days',
    value: '90d',
    getRange: () => ({
      start: startOfDay(subDays(new Date(), 89)),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: 'Last 6 months',
    value: '6m',
    getRange: () => ({
      start: startOfDay(subMonths(new Date(), 6)),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: 'Last 12 months',
    value: '12m',
    getRange: () => ({
      start: startOfDay(subMonths(new Date(), 12)),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: 'All Time',
    value: 'all_time',
    getRange: () => ({
      start: new Date(2020, 0, 1),
      end: endOfDay(new Date()),
    }),
  },
];

/* ============================================================================
   Picker Mode
   ============================================================================ */

type PickerMode = 'calendar' | 'months';

/* ============================================================================
   Calendar Grid
   ============================================================================ */

interface CalendarGridProps {
  year: number;
  month: number;
  selectionStart: Date | null;
  selectionEnd: Date | null;
  hoverDate: Date | null;
  onDateClick: (date: Date) => void;
  onDateHover: (date: Date | null) => void;
}

function CalendarGrid({
  year,
  month,
  selectionStart,
  selectionEnd,
  hoverDate,
  onDateClick,
  onDateHover,
}: CalendarGridProps) {
  const totalDays = daysInMonth(year, month);
  const startDay = firstDayOfWeek(year, month);

  // Build 6-row grid (42 cells) for consistent height
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDay; i++) {
    cells.push(null);
  }
  for (let d = 1; d <= totalDays; d++) {
    cells.push(new Date(year, month, d));
  }
  while (cells.length < 42) {
    cells.push(null);
  }

  // Determine the effective end for range highlighting (use hover if still picking)
  const effectiveEnd =
    selectionStart && !selectionEnd && hoverDate ? hoverDate : selectionEnd;

  // Normalize range direction (user might hover before start)
  let rangeStart = selectionStart;
  let rangeEnd = effectiveEnd;
  if (rangeStart && rangeEnd && rangeStart.getTime() > rangeEnd.getTime()) {
    [rangeStart, rangeEnd] = [rangeEnd, rangeStart];
  }

  return (
    <div>
      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS_OF_WEEK.map((day) => (
          <div
            key={day}
            className="h-7 flex items-center justify-center text-2xs font-medium text-text-3 select-none"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {cells.map((date, idx) => {
          if (!date) {
            return <div key={`empty-${idx}`} className="h-8" />;
          }

          const today = isToday(date);
          const future = isFuture(date);
          const isStart = rangeStart ? isSameDay(date, rangeStart) : false;
          const isEnd = rangeEnd ? isSameDay(date, rangeEnd) : false;
          const inRange =
            rangeStart && rangeEnd ? isBetween(date, rangeStart, rangeEnd) : false;
          const isEndpoint = isStart || isEnd;

          let cellClasses =
            'h-8 w-full flex items-center justify-center text-xs rounded-none transition-colors duration-100 relative select-none';

          if (future) {
            cellClasses += ' text-text-3/30 cursor-not-allowed';
          } else if (isEndpoint) {
            cellClasses +=
              ' bg-monarch-gold text-white font-semibold cursor-pointer';
          } else if (inRange) {
            cellClasses +=
              ' bg-monarch-gold/15 text-text cursor-pointer';
          } else if (today) {
            cellClasses +=
              ' text-monarch-gold font-semibold cursor-pointer hover:bg-bg-2';
          } else {
            cellClasses +=
              ' text-text-2 cursor-pointer hover:bg-bg-2';
          }

          return (
            <button
              key={date.toISOString()}
              className={cellClasses}
              disabled={future}
              onClick={() => !future && onDateClick(date)}
              onMouseEnter={() => !future && onDateHover(date)}
              onMouseLeave={() => onDateHover(null)}
              type="button"
            >
              {date.getDate()}
              {today && !isEndpoint && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-monarch-gold" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================================
   Month Picker Grid
   ============================================================================ */

interface MonthPickerGridProps {
  year: number;
  onMonthSelect: (month: number) => void;
  selectedMonth: number | null;
}

function MonthPickerGrid({ year, onMonthSelect, selectedMonth }: MonthPickerGridProps) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  return (
    <div className="grid grid-cols-3 gap-2">
      {MONTH_NAMES_SHORT.map((name, idx) => {
        const isCurrent = year === currentYear && idx === currentMonth;
        const isFutureMonth =
          year > currentYear || (year === currentYear && idx > currentMonth);
        const isSelected = selectedMonth === idx;

        let classes =
          'px-2 py-2.5 rounded-none text-xs font-medium transition-colors duration-100 text-center';

        if (isFutureMonth) {
          classes += ' text-text-3/30 cursor-not-allowed';
        } else if (isSelected) {
          classes += ' bg-monarch-gold text-white cursor-pointer';
        } else if (isCurrent) {
          classes +=
            ' text-monarch-gold bg-monarch-gold/10 cursor-pointer hover:bg-monarch-gold/20';
        } else {
          classes +=
            ' text-text-2 cursor-pointer hover:bg-bg-2';
        }

        return (
          <button
            key={name}
            className={classes}
            disabled={isFutureMonth}
            onClick={() => !isFutureMonth && onMonthSelect(idx)}
            type="button"
          >
            {name}
          </button>
        );
      })}
    </div>
  );
}

/* ============================================================================
   Main DateRangePicker Component
   ============================================================================ */

export default function DateRangePicker() {
  const dateRange = useMonarchStore((s) => s.ui.dateRange);
  const setDateRange = useMonarchStore((s) => s.setDateRange);

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<PickerMode>('calendar');

  // Calendar navigation state
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  // Selection state for the calendar (click start, click end)
  const [selectionStart, setSelectionStart] = useState<Date | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  // Which month is selected in month-picker mode (null = none)
  const [selectedMonthIdx, setSelectedMonthIdx] = useState<number | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // When dropdown opens, sync calendar view to current range end
  useEffect(() => {
    if (isOpen) {
      const end = ensureDate(dateRange.end);
      setViewYear(end.getFullYear());
      setViewMonth(end.getMonth());
      setSelectionStart(null);
      setSelectionEnd(null);
      setHoverDate(null);
      setSelectedMonthIdx(null);

      // If the current preset is "month", detect which month is selected
      if (dateRange.preset === 'month') {
        const start = ensureDate(dateRange.start);
        setSelectedMonthIdx(start.getMonth());
        setViewYear(start.getFullYear());
        setMode('months');
      } else {
        setMode('calendar');
      }
    }
  }, [isOpen]);

  // ---- Preset click handler ----
  const handlePresetClick = useCallback(
    (preset: PresetOption) => {
      const { start, end } = preset.getRange();
      const hours = diffInHours(start, end);
      const granularity = hours <= 48 ? 'hour' : 'day';

      setDateRange({
        start,
        end,
        preset: preset.value,
        granularity,
      });

      setIsOpen(false);
    },
    [setDateRange],
  );

  // ---- Calendar date click ----
  const handleDateClick = useCallback(
    (date: Date) => {
      if (!selectionStart || selectionEnd) {
        // First click: set start, clear end
        setSelectionStart(date);
        setSelectionEnd(null);
      } else {
        // Second click: set end (normalize order)
        let start = selectionStart;
        let end = date;
        if (start.getTime() > end.getTime()) {
          [start, end] = [end, start];
        }

        const hours = diffInHours(start, endOfDay(end));
        const granularity = hours <= 48 ? 'hour' : 'day';

        setDateRange({
          start: startOfDay(start),
          end: endOfDay(end),
          preset: 'custom',
          granularity,
        });

        setSelectionStart(null);
        setSelectionEnd(null);
        setIsOpen(false);
      }
    },
    [selectionStart, selectionEnd, setDateRange],
  );

  // ---- Month picker select ----
  const handleMonthSelect = useCallback(
    (monthIdx: number) => {
      const start = startOfMonth(new Date(viewYear, monthIdx, 1));
      const end = endOfMonth(new Date(viewYear, monthIdx, 1));

      setDateRange({
        start,
        end,
        preset: 'month',
        granularity: 'day',
      });

      setSelectedMonthIdx(monthIdx);
      setIsOpen(false);
    },
    [viewYear, setDateRange],
  );

  // ---- Calendar navigation ----
  const goToPrevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    const atCurrentMonth =
      viewYear === now.getFullYear() && viewMonth === now.getMonth();
    if (atCurrentMonth) return; // Don't go past current month
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, [viewYear, viewMonth, now]);

  const goToPrevYear = useCallback(() => {
    setViewYear((y) => y - 1);
  }, []);

  const goToNextYear = useCallback(() => {
    if (viewYear >= now.getFullYear()) return;
    setViewYear((y) => y + 1);
  }, [viewYear, now]);

  // ---- Compare toggle ----
  const handleToggleCompare = useCallback(() => {
    setDateRange({ compareEnabled: !dateRange.compareEnabled });
  }, [dateRange.compareEnabled, setDateRange]);

  // ---- Display label ----
  const displayLabel = useMemo(() => {
    const preset = QUICK_PRESETS.find((p) => p.value === dateRange.preset);
    if (preset) return preset.label;

    if (dateRange.preset === 'month') {
      const start = ensureDate(dateRange.start);
      return `${MONTH_NAMES[start.getMonth()]} ${start.getFullYear()}`;
    }

    if (dateRange.preset === 'this_month') return 'This Month';
    if (dateRange.preset === 'last_month') return 'Last Month';
    if (dateRange.preset === 'this_quarter') return 'This Quarter';
    if (dateRange.preset === 'today') return 'Today';
    if (dateRange.preset === 'yesterday') return 'Yesterday';

    // Custom
    return `${formatDisplayDate(dateRange.start)} - ${formatDisplayDate(dateRange.end)}`;
  }, [dateRange.preset, dateRange.start, dateRange.end]);

  // Can navigate forward?
  const canGoNextMonth =
    viewYear < now.getFullYear() ||
    (viewYear === now.getFullYear() && viewMonth < now.getMonth());
  const canGoNextYear = viewYear < now.getFullYear();

  return (
    <div className="relative" ref={dropdownRef}>
      {/* ---- Trigger Button ---- */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-xl
          bg-bg-1 border border-border
          text-xs text-text-2
          hover:border-border-2 hover:text-text
          transition-colors duration-150
        "
        aria-label="Select date range"
        type="button"
      >
        <Calendar className="w-3.5 h-3.5 text-monarch-gold" />
        <span className="font-medium truncate max-w-[120px] sm:max-w-none">{displayLabel}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-text-3 transition-transform duration-150 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* ---- Dropdown Panel ---- */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />

          <div
            className="
              absolute right-0 top-full mt-2 z-40
              rounded-xl border border-border
              bg-[var(--color-header-bg)]
              shadow-none overflow-hidden
              flex flex-col sm:flex-row
              w-[calc(100vw-2rem)] sm:w-[480px]
              max-h-[calc(100vh-5rem)] overflow-y-auto sm:overflow-y-visible
            "
          >
            {/* ---- Left: Presets ---- */}
            <div className="sm:w-[160px] border-b sm:border-b-0 sm:border-r border-[var(--color-header-border)] py-2 px-2 flex sm:flex-col shrink-0 gap-1 sm:gap-0 flex-wrap sm:flex-nowrap">
              <div className="text-2xs font-semibold text-text-3 uppercase tracking-wider px-2 py-1.5 mb-1 hidden sm:block">
                Quick Select
              </div>

              {QUICK_PRESETS.map((preset) => {
                const isActive = dateRange.preset === preset.value;
                return (
                  <button
                    key={preset.value}
                    onClick={() => handlePresetClick(preset)}
                    className={`
                      w-full text-left px-2.5 py-2 rounded-none text-xs
                      transition-colors duration-100
                      ${
                        isActive
                          ? 'bg-monarch-gold/15 text-monarch-gold font-semibold'
                          : 'text-text-2 hover:bg-bg-2 hover:text-text'
                      }
                    `}
                    type="button"
                  >
                    {preset.label}
                  </button>
                );
              })}

              {/* Divider */}
              <div className="hidden sm:block h-px bg-[var(--color-header-border)] my-2" />

              {/* Mode toggles */}
              <button
                onClick={() => setMode('calendar')}
                className={`
                  w-full text-left px-2.5 py-2 rounded-none text-xs
                  transition-colors duration-100
                  ${
                    mode === 'calendar' && dateRange.preset === 'custom'
                      ? 'bg-monarch-gold/15 text-monarch-gold font-semibold'
                      : mode === 'calendar'
                        ? 'text-monarch-gold'
                        : 'text-text-2 hover:bg-bg-2 hover:text-text'
                  }
                `}
                type="button"
              >
                Custom Range
              </button>
              <button
                onClick={() => setMode('months')}
                className={`
                  w-full text-left px-2.5 py-2 rounded-none text-xs
                  transition-colors duration-100
                  ${
                    mode === 'months'
                      ? dateRange.preset === 'month'
                        ? 'bg-monarch-gold/15 text-monarch-gold font-semibold'
                        : 'text-monarch-gold'
                      : 'text-text-2 hover:bg-bg-2 hover:text-text'
                  }
                `}
                type="button"
              >
                Pick Month
              </button>

              {/* Compare toggle + mode selector */}
              <div className="sm:mt-auto pt-2 sm:border-t border-[var(--color-header-border)]">
                <label className="flex items-center gap-2 px-2 py-1.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={dateRange.compareEnabled}
                    onChange={handleToggleCompare}
                    className="
                      w-3.5 h-3.5 rounded border-border
                      text-monarch-gold focus:ring-monarch-gold focus:ring-offset-0
                      bg-bg-card cursor-pointer
                    "
                  />
                  <span className="text-2xs text-text-3 group-hover:text-text-2 transition-colors duration-100">
                    Compare period
                  </span>
                </label>
              </div>
            </div>

            {/* ---- Right: Calendar / Month Picker ---- */}
            <div className="flex-1 p-3">
              {mode === 'calendar' ? (
                <>
                  {/* Calendar header with month/year navigation */}
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={goToPrevMonth}
                      className="p-1 rounded-none text-text-3 hover:bg-bg-2 hover:text-text transition-colors"
                      type="button"
                      aria-label="Previous month"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold text-text">
                        {MONTH_NAMES[viewMonth]}
                      </span>
                      <span className="text-sm font-medium text-text-3">
                        {viewYear}
                      </span>
                    </div>

                    <button
                      onClick={goToNextMonth}
                      disabled={!canGoNextMonth}
                      className={`p-1 rounded-none transition-colors ${
                        canGoNextMonth
                          ? 'text-text-3 hover:bg-bg-2 hover:text-text'
                          : 'text-text-3/20 cursor-not-allowed'
                      }`}
                      type="button"
                      aria-label="Next month"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Selection hint */}
                  <div className="text-2xs text-text-3 mb-2 text-center">
                    {selectionStart && !selectionEnd
                      ? 'Click another date to complete range'
                      : 'Click a date to start selecting'}
                  </div>

                  {/* Calendar grid */}
                  <CalendarGrid
                    year={viewYear}
                    month={viewMonth}
                    selectionStart={selectionStart}
                    selectionEnd={selectionEnd}
                    hoverDate={hoverDate}
                    onDateClick={handleDateClick}
                    onDateHover={setHoverDate}
                  />

                  {/* Current selection display */}
                  {dateRange.preset !== '7d' || selectionStart ? (
                    <div className="mt-3 pt-2 border-t border-[var(--color-header-border)]">
                      {selectionStart && !selectionEnd ? (
                        <div className="text-xs text-text-2 text-center">
                          <span className="text-monarch-gold font-medium">
                            {formatDisplayDate(selectionStart)}
                          </span>
                          <span className="text-text-3"> to </span>
                          <span className="text-text-3 italic">
                            {hoverDate
                              ? formatDisplayDate(hoverDate)
                              : '...'}
                          </span>
                        </div>
                      ) : (
                        <div className="text-xs text-text-2 text-center">
                          <span className="text-text-3">Selected: </span>
                          <span className="font-medium">{displayLabel}</span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  {/* Month picker header with year navigation */}
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={goToPrevYear}
                      className="p-1 rounded-none text-text-3 hover:bg-bg-2 hover:text-text transition-colors"
                      type="button"
                      aria-label="Previous year"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    <span className="text-sm font-semibold text-text">
                      {viewYear}
                    </span>

                    <button
                      onClick={goToNextYear}
                      disabled={!canGoNextYear}
                      className={`p-1 rounded-none transition-colors ${
                        canGoNextYear
                          ? 'text-text-3 hover:bg-bg-2 hover:text-text'
                          : 'text-text-3/20 cursor-not-allowed'
                      }`}
                      type="button"
                      aria-label="Next year"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="text-2xs text-text-3 mb-3 text-center">
                    Select an entire month
                  </div>

                  {/* Month grid */}
                  <MonthPickerGrid
                    year={viewYear}
                    onMonthSelect={handleMonthSelect}
                    selectedMonth={
                      dateRange.preset === 'month'
                        ? selectedMonthIdx
                        : null
                    }
                  />

                  {/* Current selection display */}
                  {dateRange.preset === 'month' && selectedMonthIdx !== null && (
                    <div className="mt-3 pt-2 border-t border-[var(--color-header-border)]">
                      <div className="text-xs text-text-2 text-center">
                        <span className="text-text-3">Selected: </span>
                        <span className="font-medium">
                          {MONTH_NAMES[selectedMonthIdx]} {viewYear}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
